/**
 * <TutorStudentActivityBar> — l'activité qu'un élève a devant lui, en une barre (molecule).
 *
 * Le titre, à la couleur de la matière (sa teinte et le liseré de l'activité en cours), comme les lignes
 * d'activité. Pas son visage (weekly du 03/11, W8) : sur une activité seule, posée dans sa carte, il ne
 * situe rien ; il ne se pose que dans une liste, sur l'activité qu'il a devant lui. La toucher ouvre
 * l'activité au centre. Lue par la carte d'un élève de la pellicule qu'on ne
 * regarde pas, et par chaque case de la mosaïque (Wajih, round 4 : « l'activité en cours de chaque élève
 * doit être visible »). Rien quand l'élève n'a rien devant lui. Le titre seul (daily du 05/11 : ni « En cours », ni
 * le quiz ou l'exercice, « trop gros, ça sert à rien ») ; le lecteur d'écran dit encore où il en est.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { SUBJECT_RAIL_WIDTH, subjectTint } from "../../utils/subjectTouch"
import { LIVE_ACTIVITY_PART, tutorStudentOnScreen } from "../TutorLiveActivityList/tutorLiveCurrentAndNext"

export type TutorStudentActivityBarProps = {
    student: UITutorLiveStudent
    /** Ouvrir l'activité au centre. Absent : la barre se lit, sans geste. */
    onOpenActivity?: (activityId: string) => void
    testID?: string
}

const TEXTS = {
    activity: (firstname: string, title: string, part?: "quiz" | "exercise") =>
        `${firstname} est sur « ${title} »${part != null ? `, ${LIVE_ACTIVITY_PART[part].toLowerCase()}` : ""}, l'ouvrir`,
}

export function TutorStudentActivityBar(props: TutorStudentActivityBarProps) {
    const { student, onOpenActivity, testID } = props
    const onScreen = tutorStudentOnScreen(student)
    if (onScreen == null) return null
    return (
        <Pressable
            onPress={onOpenActivity != null ? () => onOpenActivity(onScreen.id) : undefined}
            disabled={onOpenActivity == null}
            accessibilityRole={onOpenActivity != null ? "button" : undefined}
            accessibilityLabel={TEXTS.activity(student.firstname, onScreen.title, onScreen.part)}
            style={({ pressed }) => [styles.bar(onScreen.subject?.color), pressed && styles.pressed]}
            testID={testID}
        >
            <Text size="text-xs" weight="semibold" color="text-strong" numberOfLines={1} style={styles.flex}>
                {onScreen.title}
            </Text>
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    bar: (subject: SemanticColorKey | undefined) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["1"],
        minHeight: 24,
        paddingHorizontal: theme.spacing["1"],
        borderRadius: theme.radius.sm,
        // L'activité en cours : le liseré de sa matière, qui la détache (`subjectTouch`).
        borderLeftWidth: subject != null ? SUBJECT_RAIL_WIDTH : 0,
        borderLeftColor: subject != null ? theme.colors[subject] : "transparent",
        backgroundColor: subject != null ? subjectTint(theme.colors[subject]) : theme.colors["surface-subtle"],
    }),
    flex: { flex: 1, minWidth: 0 },
    pressed: { opacity: 0.7 },
}))
